import { useState, useEffect } from 'react';
import { Archive } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { ARCHIVE_REASONS, ARCHIVE_REASON_LABELS, type ArchiveReason } from '../utils/archiveReasons';

type Props = {
  open: boolean;
  /** How many invoices this confirms — 1 for a row action, N for a batch. */
  count: number;
  busy: boolean;
  /**
   * Set when the selected rows sit on a bill period that other invoices also
   * resolved to. Archiving retires the period, so those other invoices leave
   * the Active list too — the user has to be told before they confirm, not
   * after the rows disappear.
   */
  sharedPeriodNote?: string | null;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: ArchiveReason, note: string) => void;
};

export function ArchiveInvoiceDialog({ open, count, busy, sharedPeriodNote, error, onCancel, onConfirm }: Props) {
  const [reason, setReason] = useState<ArchiveReason | ''>('');
  const [note, setNote] = useState('');

  // A reason left over from the last archive would be applied to this one
  // without the user seeing it.
  useEffect(() => {
    if (open) { setReason(''); setNote(''); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onCancel()}>
      <DialogContent className="max-w-md" data-testid="invoice-archive-dialog">
        <DialogHeader>
          <DialogTitle>
            Archive {count} {count === 1 ? 'invoice' : 'invoices'}?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Reason *</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as ArchiveReason)}>
              <SelectTrigger data-testid="invoice-archive-reason">
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                {ARCHIVE_REASONS.map((key) => (
                  <SelectItem key={key} value={key}>{ARCHIVE_REASON_LABELS[key]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. replaced by invoice dated 14/09"
              data-testid="invoice-archive-note"
            />
          </div>

          {sharedPeriodNote && (
            <p
              data-testid="invoice-archive-shared-warning"
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              {sharedPeriodNote}
            </p>
          )}

          <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Archived invoices drop out of dashboards, reports and exports. The PDF
            is kept, and you can restore them from the Archived tab.
          </p>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!reason || busy}
              onClick={() => reason && onConfirm(reason, note)}
              data-testid="invoice-archive-confirm"
            >
              <Archive className="h-3.5 w-3.5" />
              {busy ? 'Archiving…' : 'Archive'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
